import {
  toApiDateString,
  dayAfterApiDate,
} from "@components/PayRangeDetailPage/composables/usePayRangeDetailPageState";
import { formatFairPayDate, formatFairPayMonthYear, formatFairPayVersion } from "@components/utils/fairPayFormat";

export function dayBeforeApiDate(value) {
  const iso = toApiDateString(value);
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - 1);
  return toApiDateString(d);
}

export function formatFullCurrency(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatVersionSummary(version = {}) {
  const label = formatFairPayVersion(version.versionNumber);
  const from = formatFairPayMonthYear(version.startDate) || "—";
  const to = formatFairPayMonthYear(version.endDate) || "present";
  const min = formatFullCurrency(version.minAmount);
  const max = formatFullCurrency(version.maxAmount);
  const amount = min && max ? `${min} – ${max}` : min || max || "—";
  return `${label} · ${from} – ${to} · ${amount}`;
}

export function amountBasisLabel(unitSuffix = "") {
  const unit = String(unitSuffix).split("/")[1] || "year";
  if (unit === "year") return "annual";
  if (unit === "month") return "monthly";
  if (unit === "hour") return "hourly";
  return unit;
}

export function parseAmountInput(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function formatAmountInput(value) {
  const n = parseAmountInput(value);
  if (n == null) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** Live published versions around the one being corrected (draft/voided ignored). */
export function findAdjacentVersions(versions, current) {
  const live = (Array.isArray(versions) ? versions : [])
    .filter((v) => v.status !== "draft" && v.status !== "voided")
    .slice()
    .sort((a, b) => (Number(a.versionNumber) || 0) - (Number(b.versionNumber) || 0));
  const idx = live.findIndex(
    (v) => Number(v.versionNumber) === Number(current?.versionNumber)
  );
  return {
    previous: idx > 0 ? live[idx - 1] : null,
    next: idx >= 0 && idx < live.length - 1 ? live[idx + 1] : null,
  };
}

export function dateBounds({ previous, next, startDate, endDate }) {
  return {
    startMin: previous ? dayAfterApiDate(previous.startDate) : null,
    startMax: endDate ? dayBeforeApiDate(endDate) : next?.endDate ? dayBeforeApiDate(next.endDate) : null,
    endMin: startDate ? dayAfterApiDate(startDate) : null,
    endMax: next?.endDate ? dayBeforeApiDate(next.endDate) : null,
  };
}

export function constraintCopy(min, max) {
  if (!min && !max) return "";
  if (min && max) return `Must be between ${formatFairPayDate(min)} and ${formatFairPayDate(max)}`;
  if (min) return `Must be on or after ${formatFairPayDate(min)}`;
  return `Must be on or before ${formatFairPayDate(max)}`;
}

function sameApiDate(a, b) {
  return toApiDateString(a) === toApiDateString(b);
}

/**
 * Warn only about neighbours whose boundary the correction actually moves.
 *
 * The backend pulls the previous version's end to the day before the new start
 * and the next version's start to the day after the new end (Rule 28) — but
 * only when that neighbour is not already on that boundary. An unchanged
 * neighbour is not corrected and gets no chatter entry, so it must not be
 * announced here either (ER-12600).
 */
export function buildAdjacentWarning({ previous, next, startDate, endDate }) {
  const sentences = [];
  if (previous && startDate) {
    const prevEnd = dayBeforeApiDate(startDate);
    if (prevEnd && !sameApiDate(previous.endDate, prevEnd)) {
      sentences.push(
        `This will also update Version ${previous.versionNumber} to end on ${formatFairPayDate(
          prevEnd
        )}, and it will be marked Corrected too.`
      );
    }
  }
  if (next && endDate) {
    const nextStart = dayAfterApiDate(endDate);
    if (nextStart && !sameApiDate(next.startDate, nextStart)) {
      sentences.push(
        `This will also update Version ${next.versionNumber} to start on ${formatFairPayDate(
          nextStart
        )}, and it will be marked Corrected too.`
      );
    }
  }
  if (!sentences.length) return null;
  return {
    title: "Adjacent versions are updated too",
    body: sentences.join(" "),
  };
}
